import { useSelector } from 'react-redux';

import type { UserGroupColor, UserGroupIcon } from '@frontend/api';
import {
  Box,
  Center,
  MaterialShapeAngleIcon,
  MaterialShapeChannelIcon,
  MaterialShapeHexBarIcon,
  MaterialShapeIBeamIcon,
  MaterialShapePipeIcon,
  MaterialShapePlateIcon,
  MaterialShapeRectBarIcon,
  MaterialShapeRectTubeIcon,
  MaterialShapeRoundBarIcon,
  MaterialShapeRoundRodIcon,
  MaterialShapeRoundTubeIcon,
  MaterialShapeSheetIcon,
  MaterialShapeSquareBarIcon,
  MaterialShapeSquareTubeIcon,
  Text,
  TruncateTextWithTooltip,
  useBreakpointValue,
} from '@frontend/legacy-components';

import {
  getBusinessName,
  getGroupPrimaryKey,
  getIsLoggedIn,
  getIsSuperuser,
  getUserFullName,
} from 'store/session/selectors';

import { useUserGroupLookup } from 'utils/multisite/hooks';

const ICON_PROPS = {
  boxSize: '100%',
  color: 'feature.onInteraction',
} as const;

const getSiteIcon = (icon: UserGroupIcon) => {
  switch (icon) {
    case 'angle':
      return <MaterialShapeAngleIcon {...ICON_PROPS} />;
    case 'i_beam':
      return <MaterialShapeIBeamIcon {...ICON_PROPS} />;
    case 'channel':
      return <MaterialShapeChannelIcon {...ICON_PROPS} />;
    case 'hex_bar':
      return <MaterialShapeHexBarIcon {...ICON_PROPS} />;
    case 'pipe':
      return <MaterialShapePipeIcon {...ICON_PROPS} />;
    case 'plate':
      return <MaterialShapePlateIcon {...ICON_PROPS} />;
    case 'rect_bar':
      return <MaterialShapeRectBarIcon {...ICON_PROPS} />;
    case 'rect_tube':
      return <MaterialShapeRectTubeIcon {...ICON_PROPS} />;
    case 'round_bar':
      return <MaterialShapeRoundBarIcon {...ICON_PROPS} />;
    case 'round_rod':
      return <MaterialShapeRoundRodIcon {...ICON_PROPS} />;
    case 'round_tube':
      return <MaterialShapeRoundTubeIcon {...ICON_PROPS} />;
    case 'sheet':
      return <MaterialShapeSheetIcon {...ICON_PROPS} />;
    case 'square_bar':
      return <MaterialShapeSquareBarIcon {...ICON_PROPS} />;
    case 'square_tube':
      return <MaterialShapeSquareTubeIcon {...ICON_PROPS} />;
    default:
      return null;
  }
};

type SiteIconBoxProps = {
  icon: UserGroupIcon;
  color: UserGroupColor;
};

const SiteIconBox = ({ icon, color }: SiteIconBoxProps) => (
  <Center as="span" boxSize="20px" flexShrink={0} color={`${color}.500`}>
    {getSiteIcon(icon)}
  </Center>
);

type NameplateProps = {
  isMicroAppHeader?: boolean;
  textAlign?: 'left' | 'right' | 'center';
  display?: Record<string, string> | string;
  flex?: string | number;
};

const Nameplate = ({
  isMicroAppHeader,
  textAlign,
  display = 'unset',
  flex,
}: NameplateProps) => {
  const isMobile = useBreakpointValue({
    base: true,
    tablet: true,
    desktop: false,
  });
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isSuperuser = useSelector(getIsSuperuser);
  const userGroupName = useSelector(getBusinessName);
  const userFullName = useSelector(getUserFullName);
  const activeGroupId = useSelector(getGroupPrimaryKey);

  const { getUserGroupById, availableGroups } = useUserGroupLookup();
  const activeGroupCollectionData = activeGroupId
    ? getUserGroupById(activeGroupId)
    : undefined;
  const shouldShowSiteIcon = isSuperuser || availableGroups.length > 1;

  if (!isLoggedIn) return null;

  const companyNameRow = (
    <Box
      display="flex"
      alignItems="center"
      gap="2px"
      minWidth={0}
      overflow="hidden"
    >
      {activeGroupCollectionData && shouldShowSiteIcon && (
        <SiteIconBox
          icon={activeGroupCollectionData.icon}
          color={activeGroupCollectionData.color}
        />
      )}
      <TruncateTextWithTooltip
        text={userGroupName ?? ''}
        fontWeight="medium"
        color="currentColor"
        fontFamily="default"
        size="sm"
        minWidth={0}
        pointerEvents="auto"
        cursor="pointer"
        tooltipProps={{ variant: 'compact', hasArrow: true, gutter: 13 }}
      />
    </Box>
  );

  return isMicroAppHeader && isMobile ? (
    <></>
  ) : isMicroAppHeader ? (
    <Box
      minWidth={0}
      display="flex"
      alignItems="center"
      verticalAlign="middle"
      textAlign={textAlign}
    >
      <Box
        display="flex"
        alignItems="center"
        gap="2px"
        minWidth={0}
        overflow="hidden"
        mr={1}
        pt="3px"
        flexShrink={1}
      >
        {activeGroupCollectionData && shouldShowSiteIcon && (
          <SiteIconBox
            icon={activeGroupCollectionData.icon}
            color={activeGroupCollectionData.color}
          />
        )}
        <TruncateTextWithTooltip
          text={userGroupName ?? ''}
          fontWeight="medium"
          color="currentColor"
          fontFamily="default"
          size="sm"
          minWidth={0}
          tooltipProps={{ variant: 'compact', hasArrow: true, gutter: 13 }}
        />
      </Box>
      <Text fontWeight="bold" color="currentColor" pt="2px" flexShrink={0}>
        {userFullName}
      </Text>
    </Box>
  ) : (
    <Box
      minWidth={0}
      flex={flex}
      overflow="hidden"
      verticalAlign="middle"
      textAlign={textAlign}
      display={display}
    >
      <TruncateTextWithTooltip
        text={userFullName ?? ''}
        fontWeight="bold"
        color="currentColor"
        fontFamily="default"
        tooltipProps={{ variant: 'compact', hasArrow: true, gutter: 13 }}
      />
      {companyNameRow}
    </Box>
  );
};

export default Nameplate;
